import React, { useEffect, useState } from 'react';
import { COLORS } from '../../utils/constants';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Info, 
  X,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  Shield,
  Zap,
  Bell,
  Gift,
  MessageSquare
} from 'lucide-react';

// Play notification sound
const playSound = (type) => {
  try {
    // Use Web Audio API for better sound quality
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Different sounds for different types
    switch (type) {
      case 'success':
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
        oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1); // C#6
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        break;
      case 'error':
        oscillator.frequency.setValueAtTime(220, audioContext.currentTime); // A3
        oscillator.frequency.setValueAtTime(180, audioContext.currentTime + 0.15);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        break;
      case 'warning':
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        break;
      default: // info
        oscillator.frequency.setValueAtTime(660, audioContext.currentTime); // E5
        gainNode.gain.setValueAtTime(0.06, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    }
    
    oscillator.type = 'sine';
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    // Sound not supported or blocked
  }
};

// Get icon based on notification type
const getIcon = (type) => {
  const iconProps = { size: 20 };
  
  switch (type) {
    case 'success':
    case 'trade_success':
      return <CheckCircle {...iconProps} />;
    case 'buy':
      return <TrendingUp {...iconProps} />;
    case 'sell':
      return <TrendingDown {...iconProps} />;
    case 'deposit':
      return <ArrowDownRight {...iconProps} />;
    case 'withdrawal':
      return <ArrowUpRight {...iconProps} />;
    case 'take_profit':
      return <Target {...iconProps} />;
    case 'stop_loss':
      return <Shield {...iconProps} />;
    case 'liquidation':
      return <Zap {...iconProps} />;
    case 'admin':
      return <Bell {...iconProps} />;
    case 'promo':
      return <Gift {...iconProps} />;
    case 'message':
      return <MessageSquare {...iconProps} />;
    case 'error':
      return <XCircle {...iconProps} />;
    case 'warning':
      return <AlertCircle {...iconProps} />;
    case 'info':
    default:
      return <Info {...iconProps} />;
  }
};

// Get colors based on notification type
const getColors = (type) => {
  switch (type) {
    case 'success':
    case 'trade_success':
    case 'buy':
    case 'deposit':
    case 'take_profit':
      return {
        bg: 'linear-gradient(135deg, #0ecb81 0%, #0ba360 100%)',
        border: '#0ecb81',
        icon: '#ffffff',
        text: '#ffffff'
      };
    case 'sell':
    case 'withdrawal':
      return {
        bg: 'linear-gradient(135deg, #f6465d 0%, #cf3048 100%)',
        border: '#f6465d',
        icon: '#ffffff',
        text: '#ffffff'
      };
    case 'stop_loss':
      return {
        bg: 'linear-gradient(135deg, #ff6b35 0%, #f54e00 100%)',
        border: '#ff6b35',
        icon: '#ffffff',
        text: '#ffffff'
      };
    case 'liquidation':
      return {
        bg: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
        border: '#9c27b0',
        icon: '#ffffff',
        text: '#ffffff'
      };
    case 'admin':
      return {
        bg: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
        border: '#2196f3',
        icon: '#ffffff',
        text: '#ffffff'
      };
    case 'promo':
      return {
        bg: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
        border: '#ff9800',
        icon: '#000000',
        text: '#000000'
      };
    case 'error':
      return {
        bg: 'linear-gradient(135deg, #f6465d 0%, #cf3048 100%)',
        border: '#f6465d',
        icon: '#ffffff',
        text: '#ffffff'
      };
    case 'warning':
      return {
        bg: 'linear-gradient(135deg, #f0b90b 0%, #d9a60a 100%)',
        border: '#f0b90b',
        icon: '#000000',
        text: '#000000'
      };
    case 'info':
    default:
      return {
        bg: 'linear-gradient(135deg, #1e90ff 0%, #0066cc 100%)',
        border: '#1e90ff',
        icon: '#ffffff',
        text: '#ffffff'
      };
  }
};

// Single Toast Component
const Toast = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [progress, setProgress] = useState(100);
  const colors = getColors(notification.type);
  const duration = notification.duration || 4000;

  useEffect(() => {
    // Entrance animation
    requestAnimationFrame(() => setIsVisible(true));
    
    // Play sound based on settings check (handled by NotificationContext)
    if (notification.sound !== false) {
      const soundType = ['success', 'buy', 'deposit', 'trade_success', 'take_profit'].includes(notification.type) 
        ? 'success' 
        : ['error', 'liquidation'].includes(notification.type) ? 'error' 
        : ['warning', 'stop_loss'].includes(notification.type) ? 'warning'
        : 'info';
      playSound(soundType);
    }

    // Progress bar animation
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(progressInterval);
      }
    }, 50);

    // Auto close
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => onClose(notification.id), 300);
  };

  // Get special emoji for type
  const getEmoji = (type) => {
    switch (type) {
      case 'take_profit': return '🎯';
      case 'stop_loss': return '🛑';
      case 'liquidation': return '💥';
      case 'promo': return '🎁';
      case 'admin': return '📢';
      default: return null;
    }
  };

  const emoji = getEmoji(notification.type);

  return (
    <div
      style={{
        background: colors.bg,
        borderRadius: '12px',
        padding: '14px 16px',
        paddingBottom: '18px',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        minWidth: '320px',
        maxWidth: '400px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.1)',
        transform: isVisible && !isLeaving ? 'translateX(0)' : 'translateX(120%)',
        opacity: isVisible && !isLeaving ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
      }}
      onClick={handleClose}
    >
      {/* Shine effect */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
        animation: isVisible ? 'shine 0.6s ease-out forwards' : 'none',
      }} />
      
      {/* Icon */}
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.icon,
        flexShrink: 0,
      }}>
        {getIcon(notification.type)}
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color: colors.text,
          fontSize: '14px',
          fontWeight: '700',
          marginBottom: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {emoji && <span>{emoji}</span>}
          {notification.title}
        </div>
        {notification.message && (
          <div style={{
            color: colors.text,
            fontSize: '13px',
            opacity: 0.9,
            lineHeight: '1.4'
          }}>
            {notification.message}
          </div>
        )}
        {notification.details && (
          <div style={{
            marginTop: '8px',
            padding: '8px 10px',
            backgroundColor: 'rgba(0,0,0,0.15)',
            borderRadius: '8px',
            fontSize: '12px',
            color: colors.text,
            opacity: 0.9
          }}>
            {notification.details}
          </div>
        )}
      </div>
      
      {/* Close button */}
      <button
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        style={{
          background: 'rgba(255,255,255,0.2)',
          border: 'none',
          borderRadius: '6px',
          padding: '4px',
          cursor: 'pointer',
          color: colors.icon,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.7,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => e.target.style.opacity = 1}
        onMouseLeave={(e) => e.target.style.opacity = 0.7}
      >
        <X size={14} />
      </button>

      {/* Progress bar */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '3px',
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          backgroundColor: 'rgba(255,255,255,0.5)',
          transition: 'width 0.05s linear',
        }} />
      </div>
    </div>
  );
};

// Toast Container Component
const NotificationToast = ({ notifications, removeNotification }) => {
  return (
    <>
      {/* CSS Animation */}
      <style>
        {`
          @keyframes shine {
            to {
              left: 100%;
            }
          }
        `}
      </style>
      
      {/* Toast Container */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        pointerEvents: 'none',
      }}>
        <div style={{ pointerEvents: 'auto' }}>
          {notifications.map((notification) => (
            <Toast
              key={notification.id}
              notification={notification}
              onClose={removeNotification}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default NotificationToast;
